import { EyeOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useLingui } from '@lingui/react';
import { DatePicker, Table, TableColumnsType, Form } from 'antd';
import type { FormInstance } from 'antd/es/form';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs'; // dayjs,
import { useEffect, useState, useRef } from 'react';
import { styled } from 'styled-components';
import {
  GQL_STATS,
  StoredTrackingPassedTimeByThematique,
} from '~/gql/tracking-thematique/tracking-thematique';
import { authClient } from '~/helpers/apollo';
import { TResponse, TTracking } from '~/pages/backoffice/admin/types';
import {
  compareBySite,
  getTrackingForExport,
} from '~/pages/backoffice/admin/utils';
// copied from rc-picker/lib/interface.d.ts
declare type EventValue<DateType> = DateType | null;
declare type RangeValue<DateType> =
  | [EventValue<DateType>, EventValue<DateType>]
  | null;
import { Loading } from '~/pages/components/ui/loading';
import { activities } from '~/utils/airtable/parsed-data/activities/activities';
import { thematiques } from '~/utils/airtable/parsed-data/thematiques/thematiques';
import { TThematique } from '~/utils/airtable/types/types';
import { downloadCSV } from '~/utils/exportCsv/utils';
import NestedCommonTable from '~/utils/table/NestedCommonTable';

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

function TrackingsPage() {
  const { i18n } = useLingui();
  const { RangePicker } = DatePicker;
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [sortedInfo, setSortedInfo] = useState<any>();
  const [filterInfo, setFilterInfo] = useState<any>();
  const [pageSize, setPageSize] = useState<number>(12);
  const formRef = useRef<FormInstance>(null);
  const [form] = Form.useForm();
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [trackings, setTrackings] = useState<TTracking[]>([]);
  const [yScroll] = useState(true);
  const [xScroll] = useState<string>();

  const columns: ColumnsType<StoredTrackingPassedTimeByThematique> = [
    {
      title: `${i18n._('common.site')}`,
      dataIndex: 'site',
      key: 'site',
      width: 50,
      sorter: (a, b) => a.site.localeCompare(b.site),
      sortOrder: sortedInfo?.columnKey === 'site' && sortedInfo.order,
      render: (_, { site }) => <p>{site}</p>,
    },
    {
      title: `${i18n._('common.thematique')}`,
      dataIndex: 'thematique',
      key: 'thematique',
      width: 100,
      sorter: (a, b) => a.thematique.localeCompare(b.thematique),
      sortOrder: sortedInfo?.columnKey === 'thematique' && sortedInfo.order,
      render: (_, { thematique }) => (
        <p>
          {thematiques.find((el) => el.thematique === thematique)?.name ?? ''}
        </p>
      ),
    },
    {
      title: `${i18n._('common.passedTime')}`,
      dataIndex: 'passedTime',
      key: 'passedTime',
      width: 100,
      sorter: (a, b) => a.passedTime - b.passedTime,
      sortOrder: sortedInfo?.columnKey === 'passedTime' && sortedInfo.order,
      render: (_, { passedTime }) => <p>{passedTime} sec</p>,
    },

    {
      title: (
        <>
          Nb <EyeOutlined /> total
        </>
      ),
      dataIndex: 'viewCount',
      key: 'viewCount',
      width: 50,
      sorter: (a, b) => a.viewCount - b.viewCount,
      sortOrder: sortedInfo?.columnKey === 'viewCount' && sortedInfo.order,
      render: (_, { viewCount }) => <p>{viewCount}</p>,
    },
    {
      title: (
        <>
          Nb <EyeOutlined /> terminé
        </>
      ),
      dataIndex: 'viewCountFinished',
      key: 'viewCountFinished',
      width: 50,
      sorter: (a, b) => a.viewCountFinished - b.viewCountFinished,
      sortOrder:
        sortedInfo?.columnKey === 'viewCountFinished' && sortedInfo.order,
      render: (_, { viewCountFinished }) => <p>{viewCountFinished}</p>,
    },
    {
      title: (
        <>
          Nb <EyeOutlined /> abandonné
        </>
      ),
      dataIndex: 'viewCountLeft',
      key: 'viewCountLeft',
      width: 50,
      sorter: (a, b) => a.viewCountLeft - b.viewCountLeft,
      sortOrder: sortedInfo?.columnKey === 'viewCountLeft' && sortedInfo.order,
      render: (_, { viewCountLeft }) => <p>{viewCountLeft}</p>,
    },
    {
      title: (
        <>
          Nb <QuestionCircleOutlined /> total
        </>
      ),
      dataIndex: 'questionCount',
      key: 'questionCount',
      width: 50,
      sorter: (a, b) => a.questionCount - b.questionCount,
      sortOrder: sortedInfo?.columnKey === 'questionCount' && sortedInfo.order,
      render: (_, { questionCount }) => <p>{questionCount}</p>,
    },
    {
      title: (
        <>
          Nb <QuestionCircleOutlined /> correct
        </>
      ),
      dataIndex: 'questionCountCorrect',
      key: 'questionCountCorrect',
      width: 50,
      sorter: (a, b) => a.questionCountCorrect - b.questionCountCorrect,
      sortOrder:
        sortedInfo?.columnKey === 'questionCountCorrect' && sortedInfo.order,
      render: (_, { questionCountCorrect }) => <p>{questionCountCorrect} </p>,
    },
    {
      title: (
        <>
          Nb <QuestionCircleOutlined /> incorrect
        </>
      ),
      dataIndex: 'questionCountIncorrect',
      key: 'questionCountIncorrect',
      width: 50,
      sorter: (a, b) => a.questionCountIncorrect - b.questionCountIncorrect,
      sortOrder:
        sortedInfo?.columnKey === 'questionCountIncorrect' && sortedInfo.order,
      render: (_, { questionCountIncorrect }) => (
        <p>{questionCountIncorrect} </p>
      ),
    },
  ];

  const expandedRowRender = (row: any) => {
    const columnsExpanded: TableColumnsType<TResponse> = [
      {
        title: 'Text',
        dataIndex: 'activityText',
        key: 'activityText',
        width: '82%',
        fixed: 'left',
        // sorter: (a, b) => {
        //   if (a.text < b.text) {
        //     return -1;
        //   }
        //   if (a.text > b.text) {
        //     return 1;
        //   }

        //   return 0;
        // },
        render: (_, { text }) => <p>{text}</p>,
      },
      {
        title: (
          <>
            Nb <QuestionCircleOutlined /> correct
          </>
        ),
        dataIndex: 'questionCountCorrect',
        key: 'questionCountCorrect',
        width: 100,
        fixed: 'left',
        sorter: (a, b) => a.questionCountCorrect - b.questionCountCorrect,
        render: (_, { questionCountCorrect }) => <p>{questionCountCorrect}</p>,
      },
      {
        title: (
          <>
            Nb <QuestionCircleOutlined /> incorrect
          </>
        ),
        dataIndex: 'questionCountIncorrect',
        key: 'questionCountIncorrect',
        width: 100,
        fixed: 'left',
        sorter: (a, b) => a.questionCountIncorrect - b.questionCountIncorrect,
        render: (_, { questionCountIncorrect }) => (
          <p>{questionCountIncorrect}</p>
        ),
      },
    ];

    return (
      <Table
        locale={{ emptyText: i18n._('table.empty') }}
        columns={columnsExpanded}
        dataSource={
          trackings.find((el) => el.id === row.key)
            ? trackings
                .find((_el) => _el.id === row.key)
                ?.responsesExtended.map((e: any) => {
                  return {
                    ...e,
                    key: e.text,
                    projectId: row.key,
                  };
                })
            : []
        }
        pagination={false}
      />
    );
  };

  const { refetch: fetchTrackings } = useQuery(GQL_STATS, {
    variables: {
      startDate: startDate?.setHours(0, 0, 0, 0),
      endDate: endDate?.setHours(23, 59, 59, 0),
    },
    client: authClient,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const trackingsWithOrder: TTracking[] =
        data.getStats.trackingsByThematique.map((t, ind) => {
          const currentThematique = thematiques.find(
            (el: TThematique) => el.thematique === t.thematique
          );

          const responsesExtended: TResponse[] = t.responses.map((el) => {
            const element = activities
              .filter(
                (act) =>
                  act.thematique === t.thematique &&
                  (act.site[0] === t.site ||
                    (act.site.length === 2 && act.site[1] === t.site))
              )
              .find((elem) => elem.order === el.activityOrder);

            const newResponse: TResponse = {
              activityOrder: el.activityOrder,
              text: element ? element.text : '',
              questionCountCorrect: el.questionCountCorrect,
              questionCountIncorrect: el.questionCountIncorrect,
            };
            return newResponse;
          });

          const newTracking: TTracking = {
            passedTime: t.passedTime,
            questionCount: t.questionCount,
            questionCountCorrect: t.questionCountCorrect,
            questionCountIncorrect: t.questionCountIncorrect,
            responses: t.responses,
            site: t.site,
            thematique: t.thematique,
            viewCount: t.viewCount,
            viewCountFinished: t.viewCountFinished,
            viewCountLeft: t.viewCountLeft,
            order: currentThematique ? currentThematique.order : 0,
            id: ind,
            responsesExtended: responsesExtended,
          };
          return newTracking;
        });

      setTrackings(
        trackingsWithOrder.sort((a, b) => a.order - b.order).sort(compareBySite)
      );
      setTotal(data.getStats.trackingsByThematique.length); // il n'y aura jamais plus que 12
    },
    onError: (e) => {
      console.log('erreur dans GQL__STATS', e);
    },
  });

  useEffect(() => {
    formRef.current?.resetFields();
  }, []);

  async function getStats() {
    setLoading(true);
    await fetchTrackings();
    setLoading(false);
  }

  const onRangeChange = (values: RangeValue<Dayjs>) => {
    if (values) {
      setStartDate(values[0]?.toDate());
      setEndDate(values[1]?.toDate());
    } else {
      setStartDate(undefined);
      setEndDate(undefined);
    }

    handleRefreshTrackings();
  };

  useEffect(() => {
    void getStats();

    console.log(filterInfo); // Tmp
  }, []);

  function handleRefreshTrackings() {
    void getStats();
  }

  function exportCsv() {
    const mapped = getTrackingForExport(trackings);

    const dateStart = new Date(startDate as Date);
    const dateStartFr = dateStart.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const dateEnd = new Date(endDate as Date);
    const dateEndFr = dateEnd.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    let title: string = i18n._('common.trackings');
    if (startDate && endDate)
      title = title + ' ' + dateStartFr + '-' + dateEndFr;
    downloadCSV(
      title,
      [
        i18n._('common.site'),
        i18n._('common.thematique'),
        i18n._('common.passedTime'),
        i18n._('common.viewCount'),
        i18n._('common.viewCountFinished'),
        i18n._('common.viewCountLeft'),

        i18n._('common.questionCount'),
        i18n._('common.questionCountCorrect'),
        i18n._('common.questionCountIncorrect'),
        i18n._('common.questions.text'),
      ],
      mapped.map((tr) => {
        return [
          tr.site,
          tr.thematique,
          tr.passedTime,
          tr.viewCount,
          tr.viewCountFinished,
          tr.viewCountLeft,
          tr.questionCount,
          tr.questionCountCorrect,
          tr.questionCountIncorrect,
          tr.text,
        ];
      })
    );
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <>
          <div>
            <Form
              {...layout}
              ref={formRef}
              name="trackings-range"
              form={form}
              style={{ margin: '0' }}
            >
              <SDivFilterElement>
                <Form.Item
                  name="range"
                  label={i18n._('filter.period')}
                  style={{ margin: '0' }}
                >
                  <RangePicker
                    onChange={onRangeChange}
                    value={[
                      dayjs(startDate as unknown as string),
                      dayjs(endDate as unknown as string),
                    ]}
                  />
                </Form.Item>
              </SDivFilterElement>
            </Form>
          </div>
          <div>
            <NestedCommonTable
              data={trackings}
              loading={loading}
              total={total}
              refresh={handleRefreshTrackings}
              columns={columns}
              xScroll={xScroll}
              yScroll={yScroll}
              showFooter={false}
              showHeader={true}
              headerTitle={i18n._('table.trackings.title')}
              selectionType="checkbox"
              top="none"
              bottom="bottomRight"
              exportCsv={exportCsv}
              addButton
              importButton
              exportButton
              page={page}
              pageSize={pageSize}
              expandedRowRender={expandedRowRender}
              setPage={setPage}
              setPageSize={setPageSize}
              setSortedInfo={setSortedInfo}
              setFilterInfo={setFilterInfo}
            />
          </div>
        </>
      )}
    </>
  );
}

export default TrackingsPage;

export const SDivFilterElement = styled.div`
  align-items: center;
  display: flex;
  margin: 20px 0 20px 0;
  width: 100%;

  & > *:first-of-type {
    margin-right: 10px;
  }
`;
